import {
  BiCalendar,
  BiCheckCircle,
  BiMap,
  BiShare,
  BiSolidPhotoAlbum,
  BiTrophy,
} from 'react-icons/bi';
import { FaHardDrive, FaMessage, FaPeopleGroup } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaMoneyBill, FaReceipt } from 'react-icons/fa';
import { SubscriptionType } from '../types/misc';

export enum Feature {
  EASY_SHARING = 'EASY_SHARING',
  MEMBER_ADMINISTRATION = 'MEMBER_ADMINISTRATION',
  EVENTS = 'EVENTS',
  PARTICIPATION_INSIGHTS = 'PARTICIPATION_INSIGHTS',
  TASKS = 'TASKS',
  STATS_AND_SCORES = 'STATS_AND_SCORES',
  COMMUNICATION = 'COMMUNICATION',
  CHATS = 'CHATS',
  COST_SPLITTING = 'COST_SPLITTING',
  FINANCIAL_ADMINISTRATION = 'FINANCIAL_ADMINISTRATION',
  MARKETPLACE = 'MARKETPLACE',
  PHOTO_ALBUMS = 'PHOTO_ALBUMS',
  LOCATIONS = 'LOCATIONS',
  FILES = 'FILES',
}

export type Features = {
  [K in Feature]?: boolean;
};

export const useFeatureList = () => {
  const { t } = useTranslation();
  return [
    {
      key: Feature.EASY_SHARING,
      icon: BiShare,
      title: t('page.onboarding.businessFeaturesScreen.features.easySharing.title'),
      description: t('page.onboarding.businessFeaturesScreen.features.easySharing.description'),
      group: 'sharing',
    },
    {
      key: Feature.MEMBER_ADMINISTRATION,
      icon: FaPeopleGroup,
      title: t('page.onboarding.businessFeaturesScreen.features.memberAdministration.title'),
      description: t(
        'page.onboarding.businessFeaturesScreen.features.memberAdministration.description',
      ),
      group: 'sharing',
    },
    {
      key: Feature.EVENTS,
      icon: BiCalendar,
      title: t('page.onboarding.businessFeaturesScreen.features.events.title'),
      description: t('page.onboarding.businessFeaturesScreen.features.events.description'),
      group: 'organization',
    },
    {
      key: Feature.PARTICIPATION_INSIGHTS,
      icon: BiTrophy,
      title: t('page.onboarding.businessFeaturesScreen.features.participationInsights.title'),
      description: t(
        'page.onboarding.businessFeaturesScreen.features.participationInsights.description',
      ),
      group: 'organization',
    },
    {
      key: Feature.TASKS,
      icon: BiCheckCircle,
      title: t('page.onboarding.businessFeaturesScreen.features.tasks.title'),
      description: t('page.onboarding.businessFeaturesScreen.features.tasks.description'),
      group: 'organization',
    },
    {
      key: Feature.STATS_AND_SCORES,
      icon: BiTrophy,
      title: t('page.onboarding.businessFeaturesScreen.features.statsAndScores.title'),
      description: t('page.onboarding.businessFeaturesScreen.features.statsAndScores.description'),
      group: 'organization',
    },
    {
      key: Feature.COMMUNICATION,
      icon: FaEnvelope,
      title: t('page.onboarding.businessFeaturesScreen.features.communication.title'),
      description: t('page.onboarding.businessFeaturesScreen.features.communication.description'),
      group: 'communication',
    },
    {
      key: Feature.CHATS,
      icon: FaMessage,
      title: t('page.onboarding.businessFeaturesScreen.features.chats.title'),
      description: t('page.onboarding.businessFeaturesScreen.features.chats.description'),
      group: 'communication',
    },
    {
      key: Feature.COST_SPLITTING,
      icon: FaReceipt,
      title: t('page.onboarding.businessFeaturesScreen.features.costSplitting.title'),
      description: t('page.onboarding.businessFeaturesScreen.features.costSplitting.description'),
      group: 'financial',
    },
    {
      key: Feature.FINANCIAL_ADMINISTRATION,
      icon: FaMoneyBill,
      title: t('page.onboarding.businessFeaturesScreen.features.financialAdministration.title'),
      description: t(
        'page.onboarding.businessFeaturesScreen.features.financialAdministration.description',
      ),
      group: 'financial',
    },
    {
      key: Feature.PHOTO_ALBUMS,
      icon: BiSolidPhotoAlbum,
      title: t('page.onboarding.businessFeaturesScreen.features.photoAlbums.title'),
      description: t('page.onboarding.businessFeaturesScreen.features.photoAlbums.description'),
      group: 'media',
    },
    {
      key: Feature.LOCATIONS,
      icon: BiMap,
      title: t('page.onboarding.businessFeaturesScreen.features.locations.title'),
      description: t('page.onboarding.businessFeaturesScreen.features.locations.description'),
      group: 'community',
    },
    // {
    //   key: Feature.FILES,
    //   icon: FaHardDrive,
    //   title: t('page.onboarding.businessFeaturesScreen.features.files.title'),
    //   description: t('page.onboarding.businessFeaturesScreen.features.files.description'),
    //   group: 'media',
    // },
  ];
};

export const featuresInSubscription: Record<SubscriptionType, Features> = {
  [SubscriptionType.FREE]: {
    [Feature.EASY_SHARING]: true,
    [Feature.EVENTS]: true,
    [Feature.TASKS]: true,
    [Feature.STATS_AND_SCORES]: true,
    [Feature.COST_SPLITTING]: true,
    [Feature.CHATS]: true,
    [Feature.MEMBER_ADMINISTRATION]: true,
    [Feature.PHOTO_ALBUMS]: true,
    [Feature.LOCATIONS]: true,
    [Feature.FILES]: true,
  },
  [SubscriptionType.BASE]: {
    [Feature.EASY_SHARING]: true,
    [Feature.EVENTS]: true,
    [Feature.TASKS]: true,
    [Feature.STATS_AND_SCORES]: true,
    [Feature.COST_SPLITTING]: true,
    [Feature.CHATS]: true,
    [Feature.MEMBER_ADMINISTRATION]: true,
    [Feature.COMMUNICATION]: true,
    [Feature.PARTICIPATION_INSIGHTS]: true,
    [Feature.FINANCIAL_ADMINISTRATION]: true,
    [Feature.PHOTO_ALBUMS]: true,
    [Feature.LOCATIONS]: true,
    [Feature.FILES]: true,
  },
  [SubscriptionType.PRO]: {
    [Feature.EASY_SHARING]: true,
    [Feature.EVENTS]: true,
    [Feature.TASKS]: true,
    [Feature.STATS_AND_SCORES]: true,
    [Feature.COST_SPLITTING]: true,
    [Feature.MEMBER_ADMINISTRATION]: true,
    [Feature.PARTICIPATION_INSIGHTS]: true,
    [Feature.FINANCIAL_ADMINISTRATION]: true,
    [Feature.CHATS]: true,
    [Feature.COMMUNICATION]: true,
    [Feature.PHOTO_ALBUMS]: true,
    [Feature.LOCATIONS]: true,
    [Feature.FILES]: true,
  },
};

export function getMinimumSubscriptionForFeatures(selectedFeatures: Features): SubscriptionType {
  const requiredFeatures = Object.entries(selectedFeatures)
    //eslint-disable-next-line
    .filter(([_, value]) => value)
    .map(([key]) => key as Feature);

  const needsPro = requiredFeatures.some(
    (feature) =>
      !featuresInSubscription[SubscriptionType.FREE][feature] &&
      !featuresInSubscription[SubscriptionType.BASE][feature],
  );
  if (needsPro) return SubscriptionType.PRO;

  const needsBase = requiredFeatures.some(
    (feature) => !featuresInSubscription[SubscriptionType.FREE][feature],
  );
  if (needsBase) return SubscriptionType.BASE;

  return SubscriptionType.FREE;
}
